<script setup lang="ts">
/*
 * What the search overlay shows before the visitor types: recent searches as
 * removable flat chips, recently viewed products as a tile row, and "Go to"
 * shortcuts as flat tiles. Owns no search state; recents come from
 * useProductSearch, viewed products from the recently-viewed store.
 */
import { CalendarClock, CircleHelp, Clock, LayoutGrid, Package, X } from 'lucide-vue-next';
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { forgetSearch } from '../../composables/useProductSearch';
import { useAuthStore } from '../../stores/auth';
import { useRecentlyViewedStore } from '../../stores/recently-viewed';
import GameTile from '../GameTile.vue';
import { PREORDER_LINK } from './navData';

defineProps<{ recent: string[] }>();

const { t } = useI18n();
const auth = useAuthStore();
const viewed = useRecentlyViewedStore();

const goTo = computed(() => [
  { key: 'catalog', to: '/catalog', label: t('header.nav.catalog'), icon: LayoutGrid },
  { key: 'preorders', to: PREORDER_LINK, label: t('header.nav.preorders'), icon: CalendarClock },
  { key: 'help', to: '/help', label: t('header.nav.help'), icon: CircleHelp },
  ...(auth.isAuthenticated ? [{ key: 'orders', to: '/orders', label: t('header.menu.orders'), icon: Package }] : []),
]);
</script>

<template>
  <div class="flex flex-col gap-10">
    <section v-if="recent.length" :aria-label="t('header.search.recent')">
      <h3 class="text-eyebrow mb-3">{{ t('header.search.recent') }}</h3>
      <ul class="flex flex-wrap gap-2">
        <li v-for="term in recent" :key="term" class="flex items-center rounded-full bg-bg-elev-2">
          <RouterLink :to="{ path: '/catalog', query: { q: term } }" class="flex h-10 items-center gap-2 rounded-full pl-4 pr-2 text-[14px] text-text-base hover:text-accent">
            <Clock class="h-4 w-4 text-text-faint" aria-hidden="true" />{{ term }}
          </RouterLink>
          <button type="button" class="mr-1 grid size-8 place-items-center rounded-full text-text-faint hover:bg-bg-mute hover:text-text-base" :aria-label="t('header.palette.forget', { term })" @click="forgetSearch(term)">
            <X class="h-4 w-4" aria-hidden="true" />
          </button>
        </li>
      </ul>
    </section>

    <section v-if="viewed.items.length" :aria-label="t('header.palette.recentlyViewed')">
      <h3 class="text-eyebrow mb-3">{{ t('header.palette.recentlyViewed') }}</h3>
      <div class="scroll-rail -mx-2 gap-4 px-2 py-3">
        <div v-for="p in viewed.items" :key="p.id" class="w-[240px] shrink-0 snap-start sm:w-[280px]">
          <GameTile :product="p" />
        </div>
      </div>
    </section>

    <section :aria-label="t('header.palette.goTo')">
      <h3 class="text-eyebrow mb-3">{{ t('header.palette.goTo') }}</h3>
      <ul class="grid grid-cols-2 gap-3 md:grid-cols-4">
        <li v-for="g in goTo" :key="g.key">
          <RouterLink :to="g.to" data-tile class="tile flex h-[84px] items-center gap-3 px-4 font-heading text-[14px] font-bold">
            <span class="grid size-10 shrink-0 place-items-center rounded-full bg-bg-mute text-accent"><component :is="g.icon" class="h-5 w-5" aria-hidden="true" /></span>
            {{ g.label }}
          </RouterLink>
        </li>
      </ul>
    </section>
  </div>
</template>
